import { useState } from "react";
import {BarChart3, Search, TrendingUp, TrendingDown, Lightbulb, ChevronUp, ChevronDown} from 'lucide-react';
import { seoKeywords } from "@/data/jobs";

const intentColors: Record<string, string> = {
  Transactional: "#22c55e",
  Informational: "#3b82f6",
  Navigational: "#8b5cf6",
};

const insights = [
  {
    title: "Target FIFO Keywords First",
    body: "\"FIFO mine jobs\" has the highest search volume (12,400/mo) with moderate competition (KD: 38). Create dedicated FIFO landing pages with keyword-rich job listings.",
    priority: "High",
    icon: "🎯",
  },
  {
    title: "Capture Entry-Level Intent",
    body: "\"Entry level mining jobs\" (+24% trend) and \"mining jobs no experience\" (+27%) are surging. Build an 'Inexperienced Miners' hub with content addressing common barriers.",
    priority: "High",
    icon: "📈",
  },
  {
    title: "Low-Difficulty Quick Wins",
    body: "\"Inexperienced mining roles\" (KD: 22) and \"mining trainee positions\" (KD: 26) offer fast ranking opportunities. Publish beginner guides to capture informational traffic.",
    priority: "Medium",
    icon: "⚡",
  },
  {
    title: "Coal Keyword Caution",
    body: "\"Coal mining jobs QLD\" is trending -3%. Diversify content toward metals (gold, copper, iron ore) which show positive growth trends.",
    priority: "Low",
    icon: "⚠️",
  },
];

export default function SEODashboard() {
  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState<"volume" | "difficulty" | "cpc">("volume");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [activeInsight, setActiveInsight] = useState<number | null>(null);

  // ROI calc state
  const [selectedKw, setSelectedKw] = useState(0);
  const [ctr, setCtr] = useState(3);
  const [convRate, setConvRate] = useState(2);

  const handleSort = (key: typeof sortKey) => {
    if (sortKey === key) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortKey(key);
      setSortDir("desc");
    }
  };

  const filtered = seoKeywords
    .filter((k) => k.keyword.toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => {
      const mult = sortDir === "asc" ? 1 : -1;
      return (a[sortKey] - b[sortKey]) * mult;
    });

  const kw = seoKeywords[selectedKw];
  const monthlyClicks = Math.round((kw.volume * ctr) / 100);
  const monthlyLeads = Math.round((monthlyClicks * convRate) / 100);
  const trafficValue = Math.round(monthlyClicks * kw.cpc);

  const SortIcon = ({ col }: { col: typeof sortKey }) => {
    if (sortKey !== col) return <ChevronUp size={12} className="text-gray-600" />;
    return sortDir === "asc" ? <ChevronUp size={12} style={{ color: "#f97316" }} /> : <ChevronDown size={12} style={{ color: "#f97316" }} />;
  };

  const priorityColors: Record<string, string> = { High: "#ef4444", Medium: "#f97316", Low: "#6b7280" };

  return (
    <section className="py-20 px-4 sm:px-6" style={{ background: "#0f1623" }}>
      <div className="max-w-7xl mx-auto">
        <div className="mb-10">
          <div className="flex items-center gap-2 mb-3">
            <BarChart3 size={16} style={{ color: "#f97316" }} />
            <span className="text-sm font-medium" style={{ color: "#f97316", fontFamily: "JetBrains Mono, monospace" }}>// SEO INTELLIGENCE</span>
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-2" style={{ fontFamily: "Outfit, sans-serif" }}>
            Interactive SEO Dashboard
          </h2>
          <p className="text-gray-400">Real-time keyword explorer, ROI projections, and strategic insights for mining job SEO.</p>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Keyword Explorer */}
          <div className="lg:col-span-2 bento-card p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-white" style={{ fontFamily: "Outfit, sans-serif" }}>Keyword Explorer</h3>
              <span className="text-xs text-gray-500" style={{ fontFamily: "JetBrains Mono, monospace" }}>{filtered.length} keywords</span>
            </div>
            <div className="relative mb-4">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
              <input
                type="text"
                placeholder="Filter keywords..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-8 pr-3 py-2 rounded-lg text-sm text-white placeholder-gray-500 outline-none border border-white/6"
                style={{ background: "#2e3850" }}
              />
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-gray-500 border-b border-white/5">
                    <th className="text-left py-2 pr-4 font-medium">Keyword</th>
                    <th className="text-right py-2 px-2 font-medium cursor-pointer hover:text-gray-300 whitespace-nowrap" onClick={() => handleSort("volume")}>
                      <span className="flex items-center justify-end gap-1">Vol. <SortIcon col="volume" /></span>
                    </th>
                    <th className="text-right py-2 px-2 font-medium cursor-pointer hover:text-gray-300" onClick={() => handleSort("difficulty")}>
                      <span className="flex items-center justify-end gap-1">KD <SortIcon col="difficulty" /></span>
                    </th>
                    <th className="text-right py-2 px-2 font-medium cursor-pointer hover:text-gray-300" onClick={() => handleSort("cpc")}>
                      <span className="flex items-center justify-end gap-1">CPC <SortIcon col="cpc" /></span>
                    </th>
                    <th className="text-right py-2 pl-2 font-medium">Intent</th>
                    <th className="text-right py-2 pl-2 font-medium">Trend</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((kw, i) => (
                    <tr
                      key={kw.keyword}
                      onClick={() => setSelectedKw(seoKeywords.indexOf(kw))}
                      className="border-b border-white/5 cursor-pointer hover:bg-white/3 transition-colors"
                      style={selectedKw === seoKeywords.indexOf(kw) ? { background: "rgba(249,115,22,0.06)" } : {}}
                    >
                      <td className="py-2.5 pr-4 text-gray-200 font-medium">{kw.keyword}</td>
                      <td className="py-2.5 px-2 text-right text-white" style={{ fontFamily: "JetBrains Mono, monospace" }}>{kw.volume.toLocaleString()}</td>
                      <td className="py-2.5 px-2 text-right">
                        <span className="inline-block px-1.5 py-0.5 rounded text-xs" style={{ fontFamily: "JetBrains Mono, monospace", background: kw.difficulty < 30 ? "rgba(34,197,94,0.15)" : kw.difficulty < 40 ? "rgba(249,115,22,0.15)" : "rgba(239,68,68,0.15)", color: kw.difficulty < 30 ? "#22c55e" : kw.difficulty < 40 ? "#f97316" : "#ef4444" }}>
                          {kw.difficulty}
                        </span>
                      </td>
                      <td className="py-2.5 px-2 text-right text-gray-300" style={{ fontFamily: "JetBrains Mono, monospace" }}>${kw.cpc.toFixed(2)}</td>
                      <td className="py-2.5 pl-2 text-right">
                        <span className="px-1.5 py-0.5 rounded-full text-xs" style={{ color: intentColors[kw.intent], background: `${intentColors[kw.intent]}15` }}>{kw.intent}</span>
                      </td>
                      <td className="py-2.5 pl-2 text-right">
                        <span className="flex items-center justify-end gap-0.5 text-xs" style={{ color: kw.trend.startsWith("+") ? "#22c55e" : "#ef4444", fontFamily: "JetBrains Mono, monospace" }}>
                          {kw.trend.startsWith("+") ? <TrendingUp size={10} /> : <TrendingDown size={10} />}
                          {kw.trend}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* ROI Calculator */}
          <div className="bento-card p-5">
            <h3 className="font-semibold text-white mb-4" style={{ fontFamily: "Outfit, sans-serif" }}>Traffic ROI</h3>
            <div className="p-3 rounded-lg mb-4" style={{ background: "#2e3850" }}>
              <p className="text-xs text-gray-400 mb-1">Selected Keyword</p>
              <p className="text-sm font-medium text-white">{kw.keyword}</p>
              <p className="text-xs mt-0.5" style={{ color: "#f97316", fontFamily: "JetBrains Mono, monospace" }}>{kw.volume.toLocaleString()} searches/mo</p>
            </div>
            <div className="space-y-4">
              <div>
                <div className="flex justify-between mb-1.5">
                  <span className="text-xs text-gray-400">CTR (%)</span>
                  <span className="text-xs font-medium" style={{ color: "#f97316", fontFamily: "JetBrains Mono, monospace" }}>{ctr}%</span>
                </div>
                <input type="range" min={1} max={15} step={0.5} value={ctr} onChange={(e) => setCtr(Number(e.target.value))} />
              </div>
              <div>
                <div className="flex justify-between mb-1.5">
                  <span className="text-xs text-gray-400">Conversion Rate</span>
                  <span className="text-xs font-medium" style={{ color: "#f97316", fontFamily: "JetBrains Mono, monospace" }}>{convRate}%</span>
                </div>
                <input type="range" min={0.5} max={10} step={0.5} value={convRate} onChange={(e) => setConvRate(Number(e.target.value))} />
              </div>
            </div>
            <div className="mt-5 space-y-2">
              {[
                { label: "Monthly Clicks", value: monthlyClicks.toLocaleString() },
                { label: "Monthly Leads", value: monthlyLeads.toLocaleString() },
                { label: "Traffic Value", value: `$${trafficValue.toLocaleString()}` },
              ].map((stat) => (
                <div key={stat.label} className="flex justify-between items-center p-2.5 rounded-lg" style={{ background: "#2e3850" }}>
                  <span className="text-xs text-gray-400">{stat.label}</span>
                  <span className="text-sm font-bold" style={{ color: "#f97316", fontFamily: "JetBrains Mono, monospace" }}>{stat.value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Strategic Insights */}
        <div className="mt-6">
          <div className="flex items-center gap-2 mb-4">
            <Lightbulb size={16} style={{ color: "#f97316" }} />
            <h3 className="font-semibold text-white" style={{ fontFamily: "Outfit, sans-serif" }}>Strategic Insights</h3>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {insights.map((ins, i) => (
              <button
                key={i}
                onClick={() => setActiveInsight(activeInsight === i ? null : i)}
                className="bento-card p-4 text-left transition-all"
                style={activeInsight === i ? { borderColor: "rgba(249,115,22,0.3)", background: "rgba(249,115,22,0.05)" } : {}}
              >
                <div className="flex items-start justify-between mb-2">
                  <span className="text-xl">{ins.icon}</span>
                  <span className="text-xs px-2 py-0.5 rounded-full font-medium" style={{ color: priorityColors[ins.priority], background: `${priorityColors[ins.priority]}15`, fontFamily: "JetBrains Mono, monospace" }}>
                    {ins.priority}
                  </span>
                </div>
                <h4 className="text-sm font-semibold text-white mb-1" style={{ fontFamily: "Outfit, sans-serif" }}>{ins.title}</h4>
                {activeInsight === i ? (
                  <p className="text-xs text-gray-400 leading-relaxed">{ins.body}</p>
                ) : (
                  <p className="text-xs text-gray-500">Click to expand →</p>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
